/**
 * M7 S38: Mission 3 world content, The Transferred Team (spec §6, §17; data `story/world/m3.json`).
 *
 * - **The hideout relay** (the relink port): a lane-ring repair in the `okafor-hideout` system, or a relay reboot that
 *   brings a relay with an end there online, is the relay step (`ring:okafor-hideout`). Once online the relay is an
 *   ordinary lane for the worker, so Tethys traffic routes through it (the trace).
 * - **Okafor at the hideout:** a dock at any station of the hideout system is `station:okafor-hideout`. The people
 *   logic (`okaforAfterEvent`: the trust test, the risk, custody, the rescue from the prisoner transport) runs after
 *   every story event with the worker's day and its Tethys rig count.
 * - **The lab derelict and its sealed core:** salvage at the lab site's POI (`sites.okaforLab`) is `poi:okafor-lab`.
 * - **The Charter Patrol inspection** on the way out: hailed once the core is aboard, on the next dock, scan or online
 *   day outside the lab system, until answered. Comply logs the core (data); evade is a seeded hard-scan draw (a
 *   smuggling bounty if caught); run books a bounty.
 * - **Voss's hail** (`vossHailLines`): one opener chosen by the ordering gate and the honesty gate (spec §17 rule 1),
 *   in the career stage's wording.
 *
 * Pure functions over the Career; event-driven, never per frame.
 */
import { z } from 'zod'
import type { Career } from '../../career/types'
import { guardianKillsOf, tetherSalesOf } from '../../career/logs'
import worldJson from '../../data/story/world/m3.json'
import { seededUnit } from '../../space/hash'
import { logStatus } from '../../story/logs'
import { currentStep, missionOf, type MissionContext } from '../../story/missions'
import { okaforAfterEvent } from '../../story/okafor'
import { storyItemOf } from '../../story/items'
import type { CareerStage, StoryEffect, StoryEvent, StoryResult } from '../../story/types'
import type { GameEvent } from '../events'
import { careerDay, fillTokens, inSystemOf, mergeResults, relayTouches, unchanged, type StoryWorldContent, type WorldCtx, type WorldInput } from '../world'

const id = z.string().min(1)
const stageText = z.strictObject({ leased: id, owned: id, corporation: id })

export const m3WorldSchema = z.strictObject({
  comment: z.string(),
  hideout: z.strictObject({ anchor: id, ringRef: id, stationRef: id }),
  lab: z.strictObject({ poiRef: id }),
  inspection: z.strictObject({
    step: id,
    fork: id,
    node: id,
    caughtNode: id,
    catchChance: z.number().min(0).max(1),
    salt: z.number().int().nonnegative(),
    bountyFaction: z.enum(['authority', 'tethys', 'meridian', 'varro_security', 'mutual', 'holdfast']),
    bountyEvade: z.number().int().nonnegative(),
    bountyRun: z.number().int().nonnegative(),
  }),
  voss: z.strictObject({
    node: id,
    watchRowLogs: z.array(id).min(1),
    saleWindowDays: z.number().int().positive(),
    lines: z.strictObject({
      account: stageText,
      sale: stageText,
      fighter: stageText,
    }),
  }),
})
export type M3World = z.infer<typeof m3WorldSchema>

let cached: M3World | null = null
export function m3World(): M3World {
  cached ??= m3WorldSchema.parse(worldJson)
  return cached
}

// ---------------------------------------------------------------------------------------------
// Aliases
// ---------------------------------------------------------------------------------------------

/** The story events a game event also means for Mission 3 (pushed into a new array; usually empty). */
export function m3Aliases(_career: Career, e: GameEvent, w: WorldCtx, cfg: M3World = m3World()): StoryEvent[] {
  const out: StoryEvent[] = []
  const hideout = cfg.hideout.anchor
  switch (e.kind) {
    case 'laneRingRepaired':
      if (inSystemOf(w, hideout, e.systemId))
        out.push({
          type: 'laneRepaired',
          ringId: cfg.hideout.ringRef,
          systemId: e.systemId,
        })
      break
    case 'rebootEnded':
      if (e.online && relayTouches(w, hideout, e.targetId)) {
        const sys = w.resolve(hideout)
        if (sys !== null)
          out.push({
            type: 'laneRepaired',
            ringId: cfg.hideout.ringRef,
            systemId: sys,
          })
      }
      break
    case 'docked':
      if (inSystemOf(w, hideout, e.systemId))
        out.push({
          type: 'docked',
          stationId: cfg.hideout.stationRef,
          systemId: e.systemId,
        })
      break
    case 'partSecured':
      if (e.poiId !== undefined && e.poiId === w.sites.okaforLab.poiId) {
        out.push({
          type: 'salvaged',
          componentType: e.componentType,
          partId: e.partId,
          systemId: e.systemId,
          poiId: cfg.lab.poiRef,
        })
      }
      break
    default:
      break
  }
  return out
}

// ---------------------------------------------------------------------------------------------
// The Charter Patrol inspection
// ---------------------------------------------------------------------------------------------

/** The system an event happened in (null for events without one). */
function eventSystem(ev: StoryEvent): string | null {
  switch (ev.type) {
    case 'docked':
    case 'scanned':
    case 'laneRepaired':
    case 'salvaged':
    case 'killed':
    case 'crime':
      return ev.systemId
    default:
      return null
  }
}

/**
 * True when the Patrol hails now: the inspection step is current with the core aboard, and the event is an online day
 * or a dock or scan outside the lab system (the player is on the way out).
 */
export function inspectionDue(career: Career, ev: StoryEvent, ctx: MissionContext, labSystem: string | null, cfg: M3World = m3World()): boolean {
  const step = currentStep(career, 'm3', ctx)
  const m = missionOf(career, 'm3')
  if (step === undefined || step.id !== cfg.inspection.step || m?.awaitingFork !== undefined) return false
  if (storyItemOf(career, 'report_core')?.state !== 'held') return false
  if (ev.type === 'onlineDay') return true
  if (ev.type !== 'docked' && ev.type !== 'scanned') return false
  const sys = eventSystem(ev)
  return sys !== null && sys !== labSystem
}

/** The inspection fork's world side: evade is a seeded hard-scan draw, run books a bounty. */
export function inspectionOutcome(career: Career, choiceId: string, cfg: M3World = m3World()): StoryResult<Career> {
  const c = cfg.inspection
  if (choiceId === 'run') {
    return {
      career,
      commands: [
        {
          type: 'seedBounty',
          faction: c.bountyFaction,
          amount: c.bountyRun,
          reason: 'm3:ranPatrol',
        },
      ],
      effects: [],
    }
  }
  if (choiceId === 'evade' && evadeCaught(career, cfg)) {
    return {
      career,
      commands: [
        {
          type: 'seedBounty',
          faction: c.bountyFaction,
          amount: c.bountyEvade,
          reason: 'm3:smuggling',
        },
      ],
      effects: [{ type: 'comms', speaker: 'charterPatrol', nodeId: c.caughtNode }],
    }
  }
  return unchanged(career)
}

/** The hard scan reads the mask (deterministic per career seed). */
export function evadeCaught(career: Career, cfg: M3World = m3World()): boolean {
  return seededUnit(career.seed >>> 0, 3, cfg.inspection.salt) < cfg.inspection.catchChance
}

// ---------------------------------------------------------------------------------------------
// Voss's hail (spec §17 rule 1)
// ---------------------------------------------------------------------------------------------

export type VossOpener = 'account' | 'sale' | 'fighter'

/** The stage key the line tables use (fleet notes read as a corporation). */
export function stageKey(stage: CareerStage): 'leased' | 'owned' | 'corporation' {
  return stage === 'corporation' || stage === 'fleetNotes' ? 'corporation' : stage
}

/** The ordering gate: a Watch row log has been read (Voss is never first to point). */
export function watchRowRead(career: Career, cfg: M3World = m3World()): boolean {
  return cfg.voss.watchRowLogs.some((l) => logStatus(career, l) === 'read')
}

/** The newest real tetherstone sale (not bunker stone) within the window, or undefined (the honesty gate). */
export function recentSale(career: Career, cfg: M3World = m3World()): ReturnType<typeof tetherSalesOf>[number] | undefined {
  const today = careerDay(career)
  const sales = tetherSalesOf(career)
  for (let i = sales.length - 1; i >= 0; i--) {
    const s = sales[i]
    if (s.bunker === true) continue
    if (s.day <= today && today - s.day <= cfg.voss.saleWindowDays) return s
  }
  return undefined
}

/** Which opener Voss uses now. */
export function vossOpener(career: Career, cfg: M3World = m3World()): VossOpener {
  if (!watchRowRead(career, cfg)) return 'account'
  if (recentSale(career, cfg) !== undefined) return 'sale'
  const anySale = tetherSalesOf(career).some((s) => s.bunker !== true)
  if (!anySale && guardianKillsOf(career).length > 0) return 'fighter'
  return 'account'
}

/** Names the line tokens need (systems and stations as the player reads them). */
export interface VossNames {
  system(id: string): string
  station(id: string): string
}

/** The opener's text at `stage`, its tokens from real Career data only. */
export function vossLine(career: Career, opener: VossOpener, stage: CareerStage, names: VossNames, cfg: M3World = m3World()): string {
  const text = cfg.voss.lines[opener][stageKey(stage)]
  if (opener === 'sale') {
    const s = recentSale(career, cfg)
    if (s === undefined) return cfg.voss.lines.account[stageKey(stage)]
    return fillTokens(text, {
      day: String(s.day),
      tonnes: String(Math.round(s.tonnes)),
      station: names.station(s.stationId),
    })
  }
  if (opener === 'fighter') {
    const kills = guardianKillsOf(career)
    const k = kills[kills.length - 1]
    if (k === undefined) return cfg.voss.lines.account[stageKey(stage)]
    return fillTokens(text, {
      day: String(k.day),
      system: names.system(k.systemId),
    })
  }
  return text
}

/**
 * The lines the comms panel puts before `nodeId`'s own: Voss's opener on her Mission 3 hail, else null. Her later
 * contacts call `vossPointingLine` (the sale line moved there when the gate held it back).
 */
export function vossHailLines(career: Career, nodeId: string, stage: CareerStage, names: VossNames, cfg: M3World = m3World()): string[] | null {
  if (nodeId !== cfg.voss.node) return null
  return [vossLine(career, vossOpener(career, cfg), stage, names, cfg)]
}

/** For Voss's next contact (S39's finale lines): the pointing line when the gates now allow it, else null. */
export function vossPointingLine(career: Career, stage: CareerStage, names: VossNames, cfg: M3World = m3World()): string | null {
  const opener = vossOpener(career, cfg)
  return opener === 'account' ? null : vossLine(career, opener, stage, names, cfg)
}

// ---------------------------------------------------------------------------------------------
// The hooks
// ---------------------------------------------------------------------------------------------

function comms(nodeId: string): StoryEffect {
  return { type: 'comms', speaker: 'charterPatrol', nodeId }
}

export const M3_WORLD: StoryWorldContent = {
  id: 'm3',
  aliases: (career, e, w) => m3Aliases(career, e, w),
  afterEvent(career, ev, input: WorldInput, ctx, w) {
    let r = okaforAfterEvent(career, ev, { day: input.day, tethysRigs: input.tethysRigs }, ctx)
    const labSystem = w?.sites.okaforLab.systemId ?? null
    if (inspectionDue(r.career, ev, ctx, labSystem))
      r = mergeResults(r, {
        career: r.career,
        commands: [],
        effects: [comms(m3World().inspection.node)],
      })
    return r
  },
  afterFork(career, missionId, forkId, choiceId) {
    if (missionId !== 'm3' || forkId !== m3World().inspection.fork) return unchanged(career)
    return inspectionOutcome(career, choiceId)
  },
}
